import {useEffect, useState} from "react";
import {useNavigate, useOutletContext} from "react-router-dom";
import {useAuth} from "base/user/provider";
import {PermitService} from "permit/service";

import {
    ApprovedPermitExtensionsSection,
    ApprovedPermitGeneralDataSection,
    ApprovedPermitViolationsSection,
} from "permit/presentational";
import {EntityViewSubPage} from "base/entity/components/container";
import {EntityAuditSection} from "base/entity/components/presentational/sections";
import {
    DeleteEntityButton,
    DeleteEntityDialog,
    DeleteItemDialog,
} from "base/delete/components";
import {ReturnToProcessButton, ReturnToProcessDialog} from "permit/components";
import {ButtonToolbar} from "base/layout/components";
import {usePermitExtensionCreate} from "permitextension/hooks/PermitExtensionCreateHook";

const ViewApprovedPermitSummarySubPage = () => {
    const context = useOutletContext();
    const permit = context[0];

    const auth = useAuth();
    const navigate = useNavigate();
    const {extendButton, extendDialog} = usePermitExtensionCreate(permit);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deletionError, setDeletionError] = useState(null);
    const [canBeDeletedMsg, setCanBeDeletedMsg] = useState("");
    const [isReturnToProcessDialogOpen, setIsReturnToProcessDialogOpen] =
        useState(false);
    const [returnToProcessError, setReturnToProcessError] = useState(null);

    const allowDelete = auth.hasPermission("delete-permits");
    const allowReturnToProcess = auth.hasPermission("return-to-process-permit");

    useEffect(() => {
        setDeletionError(null);
        setReturnToProcessError(null);
    }, []);

    const handleCanBeDeleted = () => {
        PermitService.canBeDeleted(permit.id)
            .then(result => {
                if (result[0]) {
                    handleDelete();
                } else {
                    setCanBeDeletedMsg(result[1]);
                    setDeletionError(result[1]);
                }
            })
            .catch(error => {
                setCanBeDeletedMsg(error);
                setDeletionError(error);
            });
    };

    const handleDelete = () => {
        PermitService.delete(permit.id)
            .then(() => {
                navigate("/permits/approved/list");
            })
            .catch(error => {
                setCanBeDeletedMsg(error);
                setDeletionError(error);
            });
    };

    const handleReturnToProcess = () => {
        PermitService.returnToProcess(permit.id)
            .then(() => {
                navigate(`/permits/applications/list/${permit.id}/payment/signature_s`);
            })
            .catch(error => {
                setReturnToProcessError(error);
            });
    };

    const sections = [
        <ApprovedPermitGeneralDataSection key="generaldata" permit={permit} />,
        <ApprovedPermitExtensionsSection
            key="extensions"
            extensions={permit?.extension_set}
            secondaryAction={extendButton}
        />,
        <ApprovedPermitViolationsSection key="violations" permitId={permit?.id} />,
        <EntityAuditSection key="audit" entity={permit} />,
        extendDialog,
    ];

    return (
        permit && (
            <EntityViewSubPage sections={sections}>
                {(allowReturnToProcess || allowDelete) && (
                    <ButtonToolbar>
                        {allowReturnToProcess && (
                            <ReturnToProcessButton
                                openDialog={() => setIsReturnToProcessDialogOpen(true)}
                            />
                        )}
                        {allowDelete && (
                            <DeleteEntityButton
                                openDialog={() => setIsDeleteDialogOpen(true)}
                                entityName={"autorização"}
                            />
                        )}
                    </ButtonToolbar>
                )}

                {allowReturnToProcess && (
                    <ReturnToProcessDialog
                        isDialogOpen={isReturnToProcessDialogOpen}
                        setIsDialogOpen={setIsReturnToProcessDialogOpen}
                        onReturnToProcess={handleReturnToProcess}
                        error={returnToProcessError}
                    />
                )}

                {allowDelete && (
                    <>
                        <DeleteItemDialog
                            isDialogOpen={isDeleteDialogOpen}
                            setIsDialogOpen={setIsDeleteDialogOpen}
                            deletionError={""}
                            onDelete={handleCanBeDeleted}
                        />
                        <DeleteEntityDialog
                            msg={canBeDeletedMsg}
                            setMsg={setCanBeDeletedMsg}
                            deletionError={deletionError}
                            onDelete={handleDelete}
                        />
                    </>
                )}
            </EntityViewSubPage>
        )
    );
};

export default ViewApprovedPermitSummarySubPage;
